export type SupportedLanguage = 'it' | 'en';

export interface SeoSectionContent {
  heading: string;
  body: string;
  ctaLabel?: string;
  ctaRoute?: string;
}

export interface SubsiteLocalizedSeoContent {
  title: string;
  description: string;
  keywords: string[];
  sectionsTitle: string;
  sections: SeoSectionContent[];
  parentDomainLabel: string;
}

export interface SubsiteSeoContent {
  territoryCode: string;
  territoryName: string;
  localized: Record<SupportedLanguage, SubsiteLocalizedSeoContent>;
}

const FALLBACK_TERRITORY_CODE = 'mugello';

const SUBSITE_SEO_CONTENT: Record<string, SubsiteSeoContent> = {
  mugello: {
    territoryCode: 'mugello',
    territoryName: 'Mugello',
    localized: {
      it: {
        title: 'Xplorer Mugello | Esperienze, eventi e luoghi da visitare',
        description: 'Scopri Mugello con guide locali, itinerari tematici, eventi stagionali e consigli pratici su natura, borghi e sapori del territorio.',
        keywords: [
          'xplorer mugello',
          'cosa vedere in mugello',
          'eventi mugello',
          'itinerari mugello',
          'esperienze locali mugello'
        ],
        sectionsTitle: 'Perche visitare il Mugello con Xplorer',
        sections: [
          {
            heading: 'Borghi, ville medicee e paesaggi autentici',
            body: 'Mugello unisce centri storici, ville storiche e panorami aperti. In questa sezione trovi spunti concreti per costruire giornate complete, con tappe coordinate e distanze realistiche.',
            ctaLabel: 'Esplora i luoghi del territorio',
            ctaRoute: '/explore'
          },
          {
            heading: 'Attivita outdoor tra colline, laghi e percorsi lenti',
            body: 'Dai percorsi naturalistici alle esperienze all aria aperta, Xplorer seleziona contenuti utili per chi cerca attivita in base a stagione, meteo e tempo disponibile.',
            ctaLabel: 'Cerca esperienze outdoor',
            ctaRoute: '/search'
          },
          {
            heading: 'Agenda locale: eventi e proposte aggiornate',
            body: 'Il sottosito Mugello valorizza iniziative locali con informazioni sintetiche e navigazione rapida, per aiutarti a trovare cosa fare oggi o nel prossimo weekend.',
            ctaLabel: 'Vai agli eventi e categorie',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'Scopri anche la piattaforma principale Xplorer'
      },
      en: {
        title: 'Xplorer Mugello | Local places, events and travel ideas',
        description: 'Explore Mugello with local insights, seasonal events and practical travel ideas across nature, villages and authentic local food.',
        keywords: [
          'xplorer mugello',
          'things to do in mugello',
          'mugello events',
          'mugello itinerary',
          'local experiences mugello'
        ],
        sectionsTitle: 'Why explore Mugello with Xplorer',
        sections: [
          {
            heading: 'Historic villages, Medici heritage and open landscapes',
            body: 'Mugello blends historic centers, heritage landmarks and broad natural views. These guides help visitors build realistic day plans with coherent stops.',
            ctaLabel: 'Browse local places',
            ctaRoute: '/explore'
          },
          {
            heading: 'Outdoor activities for every season',
            body: 'From scenic routes to slow tourism, the Mugello subsite highlights activities with practical context for weather, time availability and trip style.',
            ctaLabel: 'Find outdoor experiences',
            ctaRoute: '/search'
          },
          {
            heading: 'Local agenda with useful event context',
            body: 'Events and featured suggestions are organized for quick discovery, so visitors can identify what to do now, this week or during upcoming weekends.',
            ctaLabel: 'Open events and categories',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'See the main Xplorer platform'
      }
    }
  },
  impruneta: {
    territoryCode: 'impruneta',
    territoryName: 'Impruneta',
    localized: {
      it: {
        title: 'Xplorer Impruneta | Esperienze, borghi e artigianato',
        description: 'Scopri Impruneta con luoghi, eventi e consigli locali tra colline fiorentine, cultura della terracotta, pievi storiche e percorsi nel verde.',
        keywords: [
          'xplorer impruneta',
          'cosa vedere a impruneta',
          'eventi impruneta',
          'terracotta impruneta',
          'esperienze locali impruneta'
        ],
        sectionsTitle: 'Impruneta: idee locali per esplorare il territorio',
        sections: [
          {
            heading: 'Colline fiorentine, pievi e centro storico',
            body: 'Impruneta unisce paesaggio collinare, luoghi storici e una posizione vicina a Firenze. Xplorer raccoglie punti di interesse e suggerimenti utili per costruire visite semplici e concrete.',
            ctaLabel: 'Esplora i luoghi di Impruneta',
            ctaRoute: '/explore'
          },
          {
            heading: 'Terracotta, artigianato e produzioni locali',
            body: 'Il territorio e conosciuto per la lavorazione della terracotta e per realta locali legate a cultura, ospitalita e sapori. Le schede aiutano a trovare contenuti pertinenti senza navigazione dispersiva.',
            ctaLabel: 'Cerca esperienze e categorie',
            ctaRoute: '/search'
          },
          {
            heading: 'Eventi e proposte di stagione',
            body: 'Il sottosito Impruneta valorizza eventi, percorsi e suggerimenti aggiornati, con contenuti pensati per chi vuole scoprire il territorio in base al periodo dell anno.',
            ctaLabel: 'Vai alla ricerca locale',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'Consulta anche la piattaforma principale Xplorer'
      },
      en: {
        title: 'Xplorer Impruneta | Local places, events and craft heritage',
        description: 'Explore Impruneta through local places, events and practical ideas across the Florentine hills, terracotta heritage and historic routes.',
        keywords: [
          'xplorer impruneta',
          'things to do in impruneta',
          'impruneta events',
          'impruneta terracotta',
          'local experiences impruneta'
        ],
        sectionsTitle: 'Impruneta local content for practical discovery',
        sections: [
          {
            heading: 'Florentine hills, historic sites and local routes',
            body: 'Impruneta combines hillside landscapes, historic places and quick access from Florence. Xplorer organizes local suggestions to help visitors plan focused and realistic stops.',
            ctaLabel: 'Explore Impruneta places',
            ctaRoute: '/explore'
          },
          {
            heading: 'Terracotta, craftsmanship and local identity',
            body: 'The area is strongly connected with terracotta craft, local food and hospitality. Territory-first listings make these experiences easier to discover and compare.',
            ctaLabel: 'Search experiences',
            ctaRoute: '/search'
          },
          {
            heading: 'Seasonal events and timely local ideas',
            body: 'The Impruneta subsite highlights events, routes and suggestions with local context, helping visitors choose what to do by season and available time.',
            ctaLabel: 'Open local search',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'Visit the main Xplorer platform'
      }
    }
  },
  chianti: {
    territoryCode: 'chianti',
    territoryName: 'Chianti',
    localized: {
      it: {
        title: 'Xplorer Chianti | Esperienze tra borghi, vigne e territori',
        description: 'Esplora il Chianti con contenuti locali su cantine, borghi, eventi e itinerari per vivere il territorio in modo autentico.',
        keywords: [
          'xplorer chianti',
          'cosa vedere in chianti',
          'eventi chianti',
          'cantine chianti',
          'esperienze locali chianti'
        ],
        sectionsTitle: 'Chianti: contenuti locali utili per pianificare',
        sections: [
          {
            heading: 'Borghi storici e cultura diffusa',
            body: 'Il Chianti offre una rete di borghi con identita forti. Xplorer aggrega punti di interesse e descrizioni territoriali per orientarti senza perdere tempo.',
            ctaLabel: 'Esplora i luoghi del Chianti',
            ctaRoute: '/explore'
          },
          {
            heading: 'Cantine, sapori e produzioni locali',
            body: 'Le schede aiutano a individuare realta locali e proposte legate a enogastronomia, artigianato e ospitalita, con una lettura rapida ma informativa.',
            ctaLabel: 'Cerca esperienze e categorie',
            ctaRoute: '/search'
          },
          {
            heading: 'Eventi e suggerimenti stagionali',
            body: 'Per ridurre pagine generiche, il sottosito Chianti privilegia contenuti legati al territorio e suggerimenti coerenti con il periodo dell anno.',
            ctaLabel: 'Vai alla ricerca locale',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'Consulta anche la piattaforma principale Xplorer'
      },
      en: {
        title: 'Xplorer Chianti | Villages, wineries and local experiences',
        description: 'Discover Chianti through local content on villages, wineries, events and practical itineraries tailored to the territory.',
        keywords: [
          'xplorer chianti',
          'things to do in chianti',
          'chianti wineries',
          'chianti events',
          'chianti local experiences'
        ],
        sectionsTitle: 'Chianti local content for practical planning',
        sections: [
          {
            heading: 'Historic villages and cultural routes',
            body: 'Chianti is a dense network of villages and cultural landmarks. Xplorer organizes places and summaries to make trip planning faster and clearer.',
            ctaLabel: 'Explore Chianti places',
            ctaRoute: '/explore'
          },
          {
            heading: 'Wineries, food and local craftsmanship',
            body: 'Territory-first listings help visitors discover local wineries, food experiences and selected businesses with clear and useful details.',
            ctaLabel: 'Search experiences',
            ctaRoute: '/search'
          },
          {
            heading: 'Seasonal events with contextual guidance',
            body: 'The Chianti subsite emphasizes timely and territory-specific suggestions to avoid generic pages and improve useful discovery.',
            ctaLabel: 'Open local search',
            ctaRoute: '/search'
          }
        ],
        parentDomainLabel: 'Visit the main Xplorer platform'
      }
    }
  }
};

export function resolveSeoLanguage(language: string | null | undefined): SupportedLanguage {
  if (!language) {
    return 'en';
  }

  const normalized = language.toLowerCase();
  if (normalized.startsWith('it')) {
    return 'it';
  }

  return 'en';
}

export function getSubsiteSeoContent(
  territoryCode: string,
  language: string | null | undefined
): {
  territoryCode: string;
  territoryName: string;
  content: SubsiteLocalizedSeoContent;
} {
  const normalizedTerritoryCode = territoryCode?.toLowerCase() || FALLBACK_TERRITORY_CODE;
  const languageCode = resolveSeoLanguage(language);

  const territoryContent =
    SUBSITE_SEO_CONTENT[normalizedTerritoryCode] || SUBSITE_SEO_CONTENT[FALLBACK_TERRITORY_CODE];

  return {
    territoryCode: territoryContent.territoryCode,
    territoryName: territoryContent.territoryName,
    content: territoryContent.localized[languageCode]
  };
}
